/**
 * Simple authentication utilities
 * Uses localStorage to store session information
 */

const AUTH_STORAGE_KEY = 'auth-session';
const PHONE_E164_REGEX = /^\+\d{8,15}$/;

export interface AuthSession {
  phone: string;
  authenticatedAt: string;
}

/**
 * Store authentication session
 */
export function setAuthSession(phone: string): void {
  if (typeof window === 'undefined') return;
  
  const session: AuthSession = {
    phone,
    authenticatedAt: new Date().toISOString(),
  };
  
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
}

/**
 * Get current authentication session
 */
export function getAuthSession(): AuthSession | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return null;
    
    const session = JSON.parse(stored) as AuthSession;
    const phone = typeof session?.phone === 'string' ? session.phone.trim() : '';
    const authenticatedAt =
      typeof session?.authenticatedAt === 'string' ? session.authenticatedAt : '';

    const isValidPhone = PHONE_E164_REGEX.test(phone);
    const isValidUid = phone.startsWith('__uid__') && phone.length > '__uid__'.length;
    if ((!isValidPhone && !isValidUid) || !authenticatedAt) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }

    return session;
  } catch (error) {
    console.error('Error reading auth session:', error);
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getAuthSession() !== null;
}

/**
 * Clear authentication session (logout)
 */
export function clearAuthSession(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(AUTH_STORAGE_KEY);
}

/**
 * Clears store (localStorage + OTP) and developer (dev_session cookie) sessions.
 * Both auth flows are independent; logout should end both when leaving either portal.
 */
export async function clearAllAuthSessions(): Promise<void> {
  await Promise.all([
    fetch('/api/developer/logout', { method: 'POST' }).catch(() => undefined),
    fetch('/api/otp/logout', { method: 'POST' }).catch(() => undefined),
  ]);
  clearAuthSession();
}

/** Clears only the developer portal session (cookie + Redis). */
export async function clearDeveloperSession(): Promise<void> {
  await fetch('/api/developer/logout', { method: 'POST' }).catch(() => undefined);
}

/**
 * Get authenticated user's phone number
 */
export function getAuthenticatedPhone(): string | null {
  const session = getAuthSession();
  return session?.phone || null;
}

/**
 * Store authentication session for identifier-based users (no phone).
 * Uses synthetic phone "__uid__<userId>" as identity key.
 */
export function setAuthSessionByUserId(userId: string): void {
  setAuthSession('__uid__' + userId);
}

/**
 * Extract userId from session when user authenticated via identifier (not phone).
 * Returns null for phone-based sessions.
 */
export function getAuthenticatedUserId(): string | null {
  const phone = getAuthenticatedPhone();
  if (phone?.startsWith('__uid__')) return phone.slice('__uid__'.length);
  return null;
}

